import type {
  ActiveBet, BetId, BetOptions, BetType, CrapsSnapshot, EngineEvent, PointNumber,
  Resolution, ResolutionPostProcessor, RollResult, TableRules, TableState,
} from './types.js';
import type { EngineInput } from './replay.js';

import type { DiceSource } from './dice.js';
import { makeRollResult } from './dice.js';
import { Emitter } from '@casino/core';
import { BetIdGenerator } from './betId.js';
import { advancePhase } from './stateMachine.js';
import { canPlaceBet } from './legality.js';
import { resolveAll, type ResolverMap } from './bets/registry.js';
import { resolvePass, resolveDontPass } from './bets/line.js';
import { resolveCome, resolveDontCome } from './bets/come.js';
import { resolveOdds, resolveDontOdds } from './bets/odds.js';
import { maxOddsForLineBet, maxDontOddsForLineBet } from './bets/oddsCap.js';
import { resolvePlace } from './bets/place.js';
import { resolveBuy, resolveLay } from './bets/buyLay.js';
import { resolveField } from './bets/field.js';
import { resolveHardway } from './bets/hardway.js';
import { resolveBigSix, resolveBigEight } from './bets/bigSixEight.js';
import {
  resolveAny7, resolveYo, resolveAceDeuce, resolveAces, resolveBoxcars,
  resolveAnyCraps, resolveCAndE,
} from './bets/prop.js';
import { resolveHorn, resolveWorld, resolveHop } from './bets/horn.js';
import { resolveAtsSmall, resolveAtsTall, resolveAtsAll } from './bets/ats.js';

const DEFAULT_RESOLVERS: ResolverMap = {
  pass: resolvePass,
  dontPass: resolveDontPass,
  come: resolveCome,
  dontCome: resolveDontCome,
  odds: resolveOdds,
  dontOdds: resolveDontOdds,
  place: resolvePlace,
  buy: resolveBuy,
  lay: resolveLay,
  field: resolveField,
  hardway: resolveHardway,
  bigSix: resolveBigSix,
  bigEight: resolveBigEight,
  any7: resolveAny7,
  yo: resolveYo,
  aceDeuce: resolveAceDeuce,
  aces: resolveAces,
  boxcars: resolveBoxcars,
  anyCraps: resolveAnyCraps,
  cAndE: resolveCAndE,
  horn: resolveHorn,
  world: resolveWorld,
  hop: resolveHop,
  small: resolveAtsSmall,
  tall: resolveAtsTall,
  all: resolveAtsAll,
};

export interface BetSpec {
  type: BetType;
  number?: PointNumber;
  hopFaces?: ActiveBet['hopFaces'];
}

/**
 * Pure helper: evaluates whether a phantom bet of the given spec would resolve
 * as 'win' against `roll` given the pre-roll `state` and `rules`. Used by the
 * UI to flash winning zones regardless of whether a real bet is on them.
 */
export function previewWouldWin(
  spec: BetSpec,
  roll: RollResult,
  state: TableState,
  rules: TableRules,
): boolean {
  const fn = DEFAULT_RESOLVERS[spec.type];
  if (!fn) return false;
  // Mirror the engine's "off during come-out" default for place/buy/lay/hardway.
  // Without this, e.g. rolling a 9 to set the point would falsely flash place-9.
  const offByDefault =
    (spec.type === 'place' || spec.type === 'hardway' || spec.type === 'buy' || spec.type === 'lay')
    && state.phase === 'COME_OUT'
    && !rules.comeOutPlaceWorking;
  if (offByDefault) return false;
  const phantom: ActiveBet = {
    id: '__preview__' as BetId,
    type: spec.type,
    amount: 1,
    working: true,
    ...(spec.number !== undefined ? { number: spec.number } : {}),
    ...(spec.hopFaces !== undefined ? { hopFaces: spec.hopFaces } : {}),
  };
  return fn(phantom, roll, state, rules).outcome === 'win';
}

const REMOVABLE_BETWEEN_ROLLS = new Set<BetType>([
  'place', 'buy', 'lay', 'hardway', 'come', 'dontCome', 'field',
  'any7', 'anyCraps', 'yo', 'aces', 'boxcars', 'aceDeuce', 'cAndE',
  'horn', 'world', 'hop', 'bigSix', 'bigEight', 'odds', 'dontOdds',
]);

// "Contract" bets that remain on the layout after a win — the winnings are
// paid out, but the original stake stays on the number. Pass / Don't Pass
// stay for the next come-out per real-casino convention; place / buy / lay /
// hardway / big6/8 stay until they lose.
const CONTRACT_BETS_ON_WIN = new Set<BetType>([
  'pass', 'dontPass', 'place', 'buy', 'lay', 'hardway', 'bigSix', 'bigEight',
]);

// Point numbers on which come/dontCome bets advance from "no number" to a specific number.
const COME_ADVANCING_TOTALS = new Set<number>([4, 5, 6, 8, 9, 10]);

// Per casino convention, tableMin applies to line-style "main" bets only.
// Prop, hardway, hop, field, big 6/8 typically have lower minimums (often $1).
const TABLE_MIN_APPLIES = new Set<BetType>([
  'pass', 'dontPass', 'come', 'dontCome', 'place', 'buy', 'lay', 'odds', 'dontOdds',
]);

function hopFacesEqual(
  a: [1|2|3|4|5|6, 1|2|3|4|5|6] | undefined,
  b: [1|2|3|4|5|6, 1|2|3|4|5|6] | undefined,
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a[0] === b[0] && a[1] === b[1];
}

export interface CreateEngineOptions {
  dice: DiceSource;
  rules: TableRules;
  bankroll: number;
}

export interface Engine {
  getState(): TableState;
  subscribe(listener: (event: EngineEvent) => void): () => void;
  /**
   * Subscribe to the USER-INPUT stream — one event per public command
   * (placeBet/removeBet/forfeitBet/setWorking/roll) and nothing derived.
   * This is the minimal, replayable log of user actions; see replay.ts.
   */
  subscribeInputs(listener: (input: EngineInput) => void): () => void;
  placeBet(type: BetType, amount: number, options?: BetOptions): BetId | Error;
  removeBet(id: BetId): void;
  /**
   * Remove a bet WITHOUT refunding the stake. Used by the table-leave flow to
   * forfeit contract bets (pass/dontPass with an established point) that
   * removeBet refuses to take down. Emits BetRemoved; does not touch bankroll.
   */
  forfeitBet(id: BetId): void;
  setWorking(id: BetId, working: boolean): void;
  roll(): RollResult;
  canPlaceBet(type: BetType, options?: BetOptions): { allowed: boolean; reason?: string };
  setRules(rules: Partial<TableRules>): void;
  setDiceSource(source: DiceSource): void;
  setResolutionPostProcessor(fn: ResolutionPostProcessor): void;
  /** Returns a JSON-safe snapshot of session state (phase / point / bets / history). */
  serialize(): CrapsSnapshot;
  /** Restore a previously-serialized snapshot. Bankroll is NOT changed. */
  hydrate(snapshot: CrapsSnapshot): void;
}

export function createEngine(opts: CreateEngineOptions): Engine {
  let dice = opts.dice;
  let rules: TableRules = { ...opts.rules };
  let state: TableState = {
    phase: 'COME_OUT',
    bankroll: opts.bankroll,
    bets: [],
    history: [],
  };
  const emitter = new Emitter<EngineEvent>();
  const inputEmitter = new Emitter<EngineInput>();
  const idGen = new BetIdGenerator();
  let postProcess: ResolutionPostProcessor = (rs) => rs;

  const setBankroll = (next: number): void => {
    state = { ...state, bankroll: next };
    emitter.emit({ type: 'BankrollChanged', bankroll: next });
  };

  const placeBet = (type: BetType, amount: number, options: BetOptions = {}): BetId | Error => {
    if (!Number.isInteger(amount) || amount <= 0) {
      return new Error('amount must be a positive integer cents value');
    }
    const legality = canPlaceBet(type, options, state, rules);
    if (!legality.allowed) return new Error(legality.reason ?? 'bet not allowed');
    if (TABLE_MIN_APPLIES.has(type) && amount < rules.tableMin) {
      return new Error(`amount below table min (${rules.tableMin})`);
    }
    // Table max applies to the CUMULATIVE total on this zone, not each chip
    // placement. Without this, ten $500 chips on place-6 would sit at $5000
    // on a $1000-max table — the per-click `amount` is under the cap but the
    // zone isn't. Per-zone keying matches the bridge's chip-stack grouping
    // (type + number; hopFaces share a zone with their faces tuple).
    //
    // Vegas exception: tableMax pins LINE bets. Odds bets are governed only
    // by the table's oddsCap multiplier, which the dedicated branch below
    // already enforces. Without the skip, a $1000 come bet at 6 on a
    // $1000-max table would clip at $1000 odds instead of the 5×$1000=$5000
    // the 3-4-5x rule promises.
    if (type !== 'odds' && type !== 'dontOdds') {
      const sameZoneTotal = state.bets
        .filter((b) => b.type === type && b.number === options.number
          && hopFacesEqual(b.hopFaces, options.hopFaces))
        .reduce((sum, b) => sum + b.amount, 0);
      if (sameZoneTotal + amount > rules.tableMax) {
        return new Error(`amount above table max (${rules.tableMax})`);
      }
    }
    // Odds cap: limited by table's `oddsCap` and the parent line bet's amount.
    // Aggregate "sibling" line bets that the player perceives as a single
    // stack — pass/dontPass (only one allowed by Vegas rules but the engine
    // doesn't enforce that) and come/dontCome at the same number — so the
    // cap reflects the total parent commitment, not whichever single bet was
    // tapped as the odds-target. Existing odds chips against any sibling
    // count toward the same cap. Without this, two $25 pass-line clicks on
    // a $50 stack would compute the cap as 3×$25=$75 instead of 3×$50=$150.
    if (type === 'odds' || type === 'dontOdds') {
      const parent = state.bets.find((b) => b.id === options.parentBetId);
      if (parent) {
        const point: PointNumber | undefined =
          parent.type === 'pass' || parent.type === 'dontPass'
            ? state.point
            : parent.number;
        if (point !== undefined) {
          const siblings = (parent.type === 'pass' || parent.type === 'dontPass')
            ? state.bets.filter((b) => b.type === parent.type)
            : state.bets.filter((b) => b.type === parent.type && b.number === parent.number);
          const parentAmountTotal = siblings.reduce((sum, b) => sum + b.amount, 0);
          const parentIds = new Set(siblings.map((b) => b.id));
          const cap = type === 'odds'
            ? maxOddsForLineBet(parentAmountTotal, point, rules.oddsCap)
            : maxDontOddsForLineBet(parentAmountTotal, point, rules.oddsCap);
          const sameParentTotal = state.bets
            .filter((b) => b.type === type
              && b.parentBetId !== undefined
              && parentIds.has(b.parentBetId))
            .reduce((sum, b) => sum + b.amount, 0);
          if (sameParentTotal + amount > cap) {
            return new Error(`amount above odds cap (${cap})`);
          }
        }
      }
    }
    if (amount > state.bankroll) return new Error('insufficient bankroll');

    // ATS bets are single-stack per type — adding chips to a SMALL/TALL/ALL
    // zone that already has an active bet must MERGE into that bet, not
    // create a parallel one. Otherwise the second bet starts with an empty
    // `seen` set and the bridge's per-bet refresh would clobber the first
    // bet's lit-number progress on the next roll.
    if (type === 'small' || type === 'tall' || type === 'all') {
      const existing = state.bets.find((b) => b.type === type);
      if (existing) {
        state = {
          ...state,
          bets: state.bets.map((b) =>
            b.id === existing.id ? { ...b, amount: b.amount + amount } : b,
          ),
        };
        setBankroll(state.bankroll - amount);
        const merged = state.bets.find((b) => b.id === existing.id)!;
        emitter.emit({ type: 'BetPlaced', bet: merged });
        inputEmitter.emit({ type: 'bet_placed', betType: type, amount, options });
        return existing.id;
      }
    }

    const isOffByDefault = (type === 'place' || type === 'hardway' || type === 'buy' || type === 'lay')
      && state.phase === 'COME_OUT'
      && !rules.comeOutPlaceWorking;
    const working = options.working ?? !isOffByDefault;

    // Odds bets ride their PARENT's number, not a caller-supplied one:
    // pass/dontPass odds ride the table point; come/dontCome odds ride the
    // parent come bet's number. Deriving it here (instead of trusting
    // options.number) keeps an odds bet resolvable for EVERY caller — legality
    // already guarantees the point exists when odds are placed. Without it, an
    // odds bet placed with only a parentBetId (as the MCP agent does) gets
    // number:undefined, and resolveOdds then returns `continue` on every roll:
    // never paid on a point-made, never lost on a seven-out, never removed — an
    // immortal phantom that pollutes later shooter-turn keyframes.
    let resolvedNumber = options.number;
    if (type === 'odds' || type === 'dontOdds') {
      const oddsParent = state.bets.find((b) => b.id === options.parentBetId);
      resolvedNumber = oddsParent && (oddsParent.type === 'pass' || oddsParent.type === 'dontPass')
        ? state.point
        : oddsParent?.number;
    }

    const bet: ActiveBet = {
      id: idGen.next(),
      type,
      amount,
      working,
      ...(resolvedNumber !== undefined ? { number: resolvedNumber } : {}),
      ...(options.parentBetId !== undefined ? { parentBetId: options.parentBetId } : {}),
      ...(options.hopFaces !== undefined ? { hopFaces: options.hopFaces } : {}),
    };
    state = { ...state, bets: [...state.bets, bet] };
    setBankroll(state.bankroll - amount);
    emitter.emit({ type: 'BetPlaced', bet });
    inputEmitter.emit({ type: 'bet_placed', betType: type, amount, options });
    return bet.id;
  };

  const removeBet = (id: BetId): void => {
    const bet = state.bets.find((b) => b.id === id);
    if (!bet) return;
    if (!REMOVABLE_BETWEEN_ROLLS.has(bet.type)) {
      if ((bet.type === 'pass' || bet.type === 'dontPass') && state.phase === 'POINT') return;
    }
    // Cascade: odds bets backing this parent must be removed and refunded
    // alongside it. Otherwise removing a come bet (or any parent line bet)
    // would orphan its odds chips on the layout.
    const children = state.bets.filter((b) => b.parentBetId === id);
    const totalRefund = bet.amount + children.reduce((s, c) => s + c.amount, 0);
    state = {
      ...state,
      bets: state.bets.filter((b) => b.id !== id && b.parentBetId !== id),
    };
    // Emit BetRemoved (with bet info, captured before state filter) BEFORE
    // the refund BankrollChanged. This lets a spectator subscriber flag
    // suppressNextBankrollPop before the BankrollChanged arrives — which is
    // how the producer side already suppresses the "win pop" on refunds.
    // The bet payload also lets the bridge animate a chip-fly back to the
    // rack on the spectator side, mirroring the producer's removal visual.
    emitter.emit({ type: 'BetRemoved', id, bet });
    for (const c of children) emitter.emit({ type: 'BetRemoved', id: c.id, bet: c });
    inputEmitter.emit({ type: 'bet_removed', id });
    setBankroll(state.bankroll + totalRefund);
  };

  const forfeitBet = (id: BetId): void => {
    const bet = state.bets.find((b) => b.id === id);
    if (!bet) return;
    const children = state.bets.filter((b) => b.parentBetId === id);
    state = {
      ...state,
      bets: state.bets.filter((b) => b.id !== id && b.parentBetId !== id),
    };
    emitter.emit({ type: 'BetRemoved', id, bet });
    for (const c of children) emitter.emit({ type: 'BetRemoved', id: c.id, bet: c });
    inputEmitter.emit({ type: 'bet_forfeited', id });
    // No setBankroll — the stake is forfeited, not returned.
  };

  const setWorking = (id: BetId, working: boolean): void => {
    state = {
      ...state,
      bets: state.bets.map((b) => (b.id === id ? { ...b, working } : b)),
    };
    emitter.emit({ type: 'BetWorkingChanged', id, working });
    inputEmitter.emit({ type: 'working_set', id, working });
  };

  const advanceComeBets = (roll: RollResult, before: TableState): TableState => {
    if (roll.total === 7 || roll.total === 11 || roll.total === 2 || roll.total === 3 || roll.total === 12) {
      return before;
    }
    if (!COME_ADVANCING_TOTALS.has(roll.total)) return before;
    return {
      ...before,
      bets: before.bets.map((b) =>
        (b.type === 'come' || b.type === 'dontCome') && b.number === undefined
          ? { ...b, number: roll.total as PointNumber }
          : b,
      ),
    };
  };

  const applyResolutions = (resolutions: Resolution[]): void => {
    let bankroll = state.bankroll;
    const removedIds = new Set<BetId>();
    // Index by id so we can apply metadata patches in a single bets.map below.
    const resById = new Map(resolutions.map((r) => [r.betId, r] as const));
    for (const res of resolutions) {
      const bet = state.bets.find((b) => b.id === res.betId);
      if (!bet) continue;
      if (res.outcome === 'win') {
        // Contract bets (place/buy/lay/hardway/big6/big8) stay on the layout
        // after a win — only winnings go to the bankroll, original stake stays.
        if (CONTRACT_BETS_ON_WIN.has(bet.type)) {
          bankroll += res.payout;
        } else {
          bankroll += bet.amount + res.payout;
          removedIds.add(bet.id);
        }
      } else if (res.outcome === 'push') {
        bankroll += bet.amount;
        removedIds.add(bet.id);
      } else if (res.outcome === 'lose') {
        removedIds.add(bet.id);
      }
    }
    // Filter out removed bets AND apply any metadata patches for continuing
    // bets (first runtime user is ATS — each non-7 roll grows the seen set).
    state = {
      ...state,
      bets: state.bets
        .filter((b) => !removedIds.has(b.id))
        .map((b) => {
          const res = resById.get(b.id);
          if (!res || res.outcome !== 'continue' || !res.betMetadataPatch) return b;
          return {
            ...b,
            metadata: { ...(b.metadata ?? {}), ...res.betMetadataPatch },
          };
        }),
    };
    if (bankroll !== state.bankroll) setBankroll(bankroll);
  };

  const roll = (): RollResult => {
    // Compute the roll BEFORE emitting RollStarted so the event can carry
    // the result. The spectator uses this to start the dice fly on event
    // arrival instead of waiting for RollResolved (which may not arrive
    // for several seconds on a flaky WS). Producer-side dice motion is
    // user-initiated and unaffected.
    const { d1, d2 } = dice.roll();
    const result = makeRollResult(d1, d2, state.point);
    emitter.emit({ type: 'RollStarted', roll: result });

    const rawResolutions = resolveAll(state, result, rules, DEFAULT_RESOLVERS);
    const resolutions = postProcess(rawResolutions, { roll: result, state });
    applyResolutions(resolutions);

    const transition = advancePhase(state, result);
    state = transition.nextState;

    const togglesWorkingByPhase = (b: ActiveBet) =>
      b.type === 'place' || b.type === 'buy' || b.type === 'lay' || b.type === 'hardway';
    if (transition.event === 'pointEstablished') {
      // Place/buy/lay/hardway turn ON automatically when point is established.
      // Emit BetWorkingChanged for any bet whose flag actually flipped so the
      // bridge can repaint OFF/ON markers without diffing engine state itself.
      const flipped: Array<{ id: BetId; working: boolean }> = [];
      state = {
        ...state,
        bets: state.bets.map((b) => {
          if (!togglesWorkingByPhase(b) || b.working) return b;
          flipped.push({ id: b.id, working: true });
          return { ...b, working: true };
        }),
      };
      if (state.point !== undefined) {
        emitter.emit({ type: 'PointEstablished', point: state.point });
      }
      emitter.emit({ type: 'PhaseChanged', phase: 'POINT' });
      for (const f of flipped) emitter.emit({ type: 'BetWorkingChanged', id: f.id, working: f.working });
    } else if (transition.event === 'pointMade' || transition.event === 'sevenOut') {
      // Returning to COME_OUT: place/buy/lay/hardway revert to off-by-default
      // unless rules.comeOutPlaceWorking is true.
      const flipped: Array<{ id: BetId; working: boolean }> = [];
      if (!rules.comeOutPlaceWorking) {
        state = {
          ...state,
          bets: state.bets.map((b) => {
            if (!togglesWorkingByPhase(b) || !b.working) return b;
            flipped.push({ id: b.id, working: false });
            return { ...b, working: false };
          }),
        };
      }
      if (transition.event === 'pointMade') {
        emitter.emit({ type: 'PointMade' });
      } else {
        emitter.emit({ type: 'SevenOut' });
      }
      emitter.emit({ type: 'PhaseChanged', phase: 'COME_OUT' });
      for (const f of flipped) emitter.emit({ type: 'BetWorkingChanged', id: f.id, working: f.working });
    }

    state = advanceComeBets(result, state);

    emitter.emit({ type: 'RollResolved', roll: result, resolutions });
    inputEmitter.emit({ type: 'roll', d1: result.d1, d2: result.d2 });
    return result;
  };

  const serialize = (): CrapsSnapshot => ({
    phase: state.phase,
    ...(state.point !== undefined ? { point: state.point } : {}),
    bets: state.bets.map((b) => ({ ...b })),
    history: state.history.map((r) => ({ ...r })),
    bankroll: state.bankroll,
    nextBetSeq: idGen.current(),
  });

  const hydrate = (snapshot: CrapsSnapshot): void => {
    // Build the new state explicitly. Critical: `point` MUST be set to
    // whatever the snapshot has, including undefined. The earlier
    // conditional-spread pattern preserved the previous state.point when
    // snapshot.point was undefined — so a hydrate that should clear the
    // point (e.g. spectator's TableEntered after the producer's PointMade)
    // would leave the engine stuck on the old point, the bridge would
    // emit PointEstablished from serialize() on the next hydrate, and the
    // spectator's puck would freeze on the wrong number.
    const next: TableState = {
      phase: snapshot.phase,
      bets: snapshot.bets.map((b) => ({ ...b })),
      history: snapshot.history.map((r) => ({ ...r })),
      bankroll: snapshot.bankroll !== undefined ? snapshot.bankroll : state.bankroll,
    };
    if (snapshot.point !== undefined) next.point = snapshot.point;
    state = next;
    // Advance the id generator past any restored bet IDs so the next placeBet
    // can't collide. Bet IDs are formatted "bet-<n>"; parse the suffix.
    let maxN = 0;
    for (const b of snapshot.bets) {
      const match = /^bet-(\d+)$/.exec(b.id);
      if (match) { const n = Number(match[1]); if (n > maxN) maxN = n; }
    }
    idGen.seedAtLeast(Math.max(maxN, snapshot.nextBetSeq ?? 0));
    // Replay events so the bridge/UI can rebuild its view.
    //
    // We DELIBERATELY skip emitting BetPlaced for each restored bet here.
    // Two reasons:
    //  1. Subscribers that animate per-bet (chip-fly) would queue N
    //     concurrent animations on every hydrate — visually messy and
    //     prone to dropped chips when any promise rejects.
    //  2. PhaseChanged (emitted below) is enough to drive a chip-stack
    //     repaint via refreshChipStacks; restored bets reach the felt
    //     that way without misleading "newly placed" motion.
    // Live BetPlaced events still emit normally via placeBet — only the hydrate replay is silenced.
    emitter.emit({ type: 'PhaseChanged', phase: state.phase });
    if (state.point !== undefined) emitter.emit({ type: 'PointEstablished', point: state.point });
    if (snapshot.bankroll !== undefined) {
      emitter.emit({ type: 'BankrollChanged', bankroll: state.bankroll });
    }
  };

  return {
    getState: () => state,
    subscribe: (l) => emitter.subscribe(l),
    subscribeInputs: (l) => inputEmitter.subscribe(l),
    placeBet,
    removeBet,
    forfeitBet,
    setWorking,
    roll,
    canPlaceBet: (type, options = {}) => canPlaceBet(type, options, state, rules),
    setRules: (next) => { rules = { ...rules, ...next }; },
    setDiceSource: (source) => { dice = source; },
    setResolutionPostProcessor: (fn) => { postProcess = fn; },
    serialize,
    hydrate,
  };
}
